import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { GVP, GVPForm, UserData, UserProfile } from "../models/types";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Checkbox,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import {
  deleteGVPLine,
  getGVP,
  getGVPStatus,
  setGVPEdit,
  updateGVP,
  updateGVPLater,
} from "../store/users/action";
import { connect } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { AnyAction } from "redux";
import { ApplicationState } from "../store";
import { FormattedMessage, useIntl } from "react-intl";
import InputTextField from "./TextField";
import ProfileDocumentViewer from "./ProfileDocumentViewer";
import InfoIcon from "@material-ui/icons/Info";
import ProfileSignDocument from "./ProfileSignDocument";
import { sendSign } from "../store/sign/action";
import { theme } from "../theme/retrostyle";
import InputSelectField from "./SelectField";
import { getDay } from "date-fns";
import DatePicker from "react-datepicker";
import "./styles/datepicker.css";
import moment from "moment";
import lv from "date-fns/locale/lv";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import ProfileSpinner from "./ProfileSpinner";
import { toInitCap } from "../utils/text";


const isWeekday = (date: Date) => {
  const day: number = getDay(date);
  return day !== 0;
};

const ssnPattern = /^\d{6}-\d{5}$/;

const hasGVPFormData = (row: GVPForm) =>
  Boolean(
    row.ssn ||
      row.role ||
      row.name ||
      row.surname ||
      row.phone ||
      row.email ||
      row.sign ||
      row.receive ||
      row.payment
  );

const normalizeGVPForm = (form?: GVPForm[]) =>
  form
    ?.filter(hasGVPFormData)
    .map((row) => ({
      ...row,
      name: toInitCap(row.name || ""),
      surname: toInitCap(row.surname || ""),
      email: (row.email || "").toLocaleLowerCase(),
    }));

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    heading: {
      flexBasis: "100%",
      flexShrink: 0,
      fontSize: "14px",
      fontWeight: "bold",
      color: "#333333",
    },
    red: {
      color: "#EC574E",
      fontSize: "30px",
      "&:hover": {
        cursor: "pointer",
        color: "#da2232",
      },
    },
    titleBig: {
      marginBottom: 2,
      fontWeight: 600,
      padding: "2rem",
      color: "#333333",
      textAlign: "center",
    },
    infoText: {
      fontWeight: 400,
      lineHeight: 2,
      paddingBottom: "2rem",
      color: "#333333",
      textAlign: "center",
    },
    tableHeader: {
      borderBottom: "1px solid #E3E3E3",
      padding: "0px",
    },
    tableTitle: {
      fontStyle: "normal",
      fontWeight: "normal",
      fontSize: "10px",
      lineHeight: "16px",
      letterSpacing: "1.5px",
      textTransform: "uppercase",
      color: "#858585",
    },
    tableText: {
      fontStyle: "normal",
      fontWeight: "normal",
      fontSize: "14px",
      lineHeight: "16px",
      letterSpacing: "1.5px",
      textTransform: "uppercase",
      color: "#333333",
    },
    statusGreen: {
      background: "#D7EC84",
      fontSize: "12px",
      padding: "6px",
    },
    statusRed: {
      background: "#EC574E",
      fontSize: "12px",
      padding: "6px",
      color: "#ffffff",
    },
    statusGrey: {
      backgroundColor: "#E3E3E3 !important",
      fontSize: "12px",
      padding: "6px",
    },
    button: {
      width: "300px",
      backgroundColor: "#da2232",
      margin: "10px",
      color: "#ffffff",
      borderRadius: "0px",
      "&:hover": {
        cursor: "pointer",
        backgroundColor: "#9b1b25",
        color: "#ffffff",
      },
    },
    buttonInfo: {
      width: "37px",
      backgroundColor: "#ffffff",
      margin: "10px",
      color: "#333333",
      borderRadius: "0px",
      "&:hover": {
        cursor: "pointer",
        backgroundColor: "#ffffff",
        color: "#333333",
      },
    },
    menuButtonDark: {
      width: "300px",
      backgroundColor: "#333333",
      color: "#ffffff",
      border: "0px",
      margin: "10px",
      borderRadius: "0px",
      textDecoration: "none",
      textAlign: "center",
      "&:hover": {
        cursor: "pointer",
        backgroundColor: "#333333",
        color: "#ffffff",
      },
    },
    checkbox: {
      color: "#333333",
    },
    back: {
      fontSize: "15px",
      fontWeight: "bold",
      color: "#666666",
      paddingBottom: "16px",
      textDecoration: "underline",
      "&:hover": {
        cursor: "pointer",
        color: "#000000",
      },
    },
    backIcon: {
      fontSize: "15px",
      fontWeight: "bold",
      color: "#666666",
      paddingBottom: "16px",
      "&:hover": {
        cursor: "pointer",
        color: "#000000",
      },
    },
    title: {
      marginBottom: 2,
      height: "29px",
      fontSize: "14px",
      letterSpacing: "0.25px",
      color: "#333333",
      textAlign: "left",
    },
    hr: {
      border: "none",
      height: "1px",
      color: "#e3e3e3",
      backgroundColor: "#e3e3e3",
    },
    gContainer: {
      minHeight: "70vh",
    },
    inputInput: {
      transition: theme.transitions.create("width"),
      paddingLeft: `calc(0.1em + ${theme.spacing(1)}px)`,
      width: "100px",
      borders: "0px",
      background: "#ffffff",
      borderTop: "0px",
      borderRight: "0px",
      borderLeft: "0px",
      fontSize: "16px",
      color: "#333333",
      borderBottomColor: "#333333",
    },
    labelError: {
      color: "red",
      alignSelf: "center",
      fontSize: "12px",
    },
  })
);

interface propsFromDispatch {
  getGVP: () => any;
  getGVPStatus: () => any;
  setGVPEdit: () => any;
  updateGVP: (payload: any) => any;
  updateGVPLater: (payload: any) => any;
  deleteGVPLine: (payload: any) => any;
}
interface PropsFromState {
  gvp?: GVP;
  gvpLoading: boolean;
  login: boolean;
  profile: UserProfile;
  user: UserData;
}

interface IProps {
  setNewGVP: (value: number) => any;
}

type AllProps = PropsFromState & propsFromDispatch & IProps;

const ProfileGVP: React.FC<AllProps> = ({
  gvp,
  gvpLoading,
  login,
  profile,
  user,
  getGVP,
  setGVPEdit,
  getGVPStatus,
  updateGVP,
  deleteGVPLine,
  setNewGVP,
  updateGVPLater,
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const [expiryDate, setExpiryDate] = useState<Date>();
  const [formError, setFormErrors] = useState<any>({});
  const [formState, setFormState] = useState<GVP>({});
  const [isDocumentOpen, setIsDocument] = useState(false);
  const [signDocument, setSignDocument] = useState(false);
  const [waitingForStatusChange, setWaitingForStatusChange] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string>();
  const [hash, setHash] = useState("");
  const previousStatus = useRef<string>();
  const actionBoxRef = useRef<HTMLDivElement>(null);
  const signReasons: any = [
    {code: "valdes loceklis", value: {"lv": "valdes loceklis", "en": "board member", "ee": "juhatuse liige", "lt": "valdybos narys", "ru": "член правления", "de": "Vorstandsmitglied"}},
    {code: "valdes priekšsēdētājs", value: {"lv": "valdes priekšsēdētājs", "en": "chairperson of the board", "ee": "juhatuse esimees", "lt": "valdybos pirmininkas", "ru": "председатель правления", "de": "Vorstandsvorsitzender"}},
    {code: "prokūrists", value: {"lv": "prokūrists", "en": "procurator", "ee": "prokurist", "lt": "prokuristas", "ru": "прокурист", "de": "Prokurist"}},
    {code: "īpašnieks", value: {"lv": "īpašnieks", "en": "owner", "ee": "omanik", "lt": "savininkas", "ru": "владелец", "de": "Eigentümer"}},
    {code: "savs variants", value: {"lv": "savs variants", "en": "custom option", "ee": "oma valik", "lt": "savas variantas", "ru": "свой вариант", "de": "eigene Option"}}
  ];

  const signRole: any = [
    {code: "statūtu", value: {"lv": "statūtu", "en": "statutes", "ee": "põhikirja", "lt": "įstatų", "ru": "устава", "de": "Satzung"}},
    {code: "pilnvarojuma", value: {"lv": "pilnvarojuma", "en": "authorization", "ee": "volituse", "lt": "įgaliojimo", "ru": "доверенности", "de": "Vollmacht"}},
    {code: "īpašumtiesības", value: {"lv": "īpašumtiesības", "en": "ownership rights", "ee": "omandiõiguse", "lt": "nuosavybės teisės", "ru": "права собственности", "de": "Eigentumsrechte"}},
    {code: "prokūras", value: {"lv": "prokūras", "en": "procuration", "ee": "prokuura", "lt": "prokūros", "ru": "прокуры", "de": "Prokura"}},
    {code: "pārstāvības", value: {"lv": "pārstāvības", "en": "representation", "ee": "esindusõiguse", "lt": "atstovavimo", "ru": "представительства", "de": "Vertretungsbefugnis"}},
    {code: "nolikuma", value: {"lv": "nolikuma", "en": "regulations", "ee": "eeskirja", "lt": "nuostatų", "ru": "положения", "de": "Regelung"}},
    {code: "rīkojuma", value: {"lv": "rīkojuma", "en": "order", "ee": "korralduse", "lt": "įsakymo", "ru": "распоряжения", "de": "Anordnung"}},
  ];

  useEffect(() => {
    getGVP();
    // Initial load only; rerunning this while editing can overwrite draft fields after validation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (gvp) {
      setFormState({});
      setFormState({
        ...gvp,
        form: normalizeGVPForm(gvp.form),
      });
      if (gvp?.endDate) setExpiryDate(moment(gvp?.endDate, "DD.MM.YYYY").toDate())
    }
  }, [gvp]);

  useEffect(() => {
    if (!expiryDate) return;

    setFormState((current) => ({
      ...current,
      endDate: moment(expiryDate).format("DD.MM.YYYY"),
    }));
  }, [expiryDate]);

  useEffect(() => {
    if (gvp?.status === "REPORT" && login) {
      const interval = window.setInterval(() => {
        getGVPStatus();
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [getGVPStatus, gvp?.status, login]);

  useEffect(() => {
    if (
      waitingForStatusChange &&
      pendingStatus &&
      gvp?.status &&
      gvp.status !== pendingStatus
    ) {
      setWaitingForStatusChange(false);
      setPendingStatus(undefined);
    }
  }, [gvp?.status, pendingStatus, waitingForStatusChange]);

  useEffect(() => {
    if (previousStatus.current === "REPORT" && gvp?.status === "SIGN") {
      actionBoxRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }

    previousStatus.current = gvp?.status;
  }, [gvp?.status]);

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormState({
      ...formState,
      [name]: value,
    });
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const { checked, name, value, type } = e.currentTarget;
    const fieldValue = name === "signName"
      ? toInitCap(value)
      : name === "sendEmailAddress"
        ? value.toLocaleLowerCase()
        : value;
    const setValue = type === "checkbox" ? checked === true ? 1 : 0 : fieldValue;
    setFormState({
      ...formState,
      [name]: setValue,
    });
  };

  const handleFormChange = (
    e: ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const { checked, name, value, type } = e.currentTarget;
    const fieldValue = ["name", "surname"].includes(name)
      ? toInitCap(value)
      : name === "email"
        ? value.toLocaleLowerCase()
        : value;
    const setValue = type === "checkbox" ? checked === true ? 1 : 0 : fieldValue;
    if (formState.form) {
      const newForm: GVPForm[] = formState.form.map((row, rindex) => {
        if (rindex === index) {
          return {
            ...row,
            [name]: setValue,
          };
        } else {
          return row;
        }
      });

      setFormState({
        ...formState,
        form: newForm,
      });
    }
  };

  const addRow = () => {
    let gvp: GVPForm = {
      id: 0,
      ssn: "",
      name: "",
      role: "",
      surname: "",
      phone: "",
      email: "",
      sign: false,
      receive: false,
      payment: false,
    };

    let employees = formState;
    employees.form?.push(gvp);
    setFormState({ ...formState, form: employees.form });
  };

  const getNormalizedFormState = () => ({
    ...formState,
    signName: toInitCap(formState.signName || ""),
    sendEmailAddress: (
      formState.sendEmailAddress || user.profile.email || ""
    ).toLocaleLowerCase(),
    form: normalizeGVPForm(formState.form),
  });

  const handleValidation = () => {
    let formIsValid = true;
    let error: any = {};

    if (!formState.signReason) {
      formIsValid = false;
      error = { ...error, signReason: "field.empty" };
    }

    if (!formState.signName) {
      formIsValid = false;
      error = { ...error, signName: "field.empty" };
    }

    if (!formState.signRole) {
      formIsValid = false;
      error = { ...error, signRole: "field.empty" };
    }

    if (!expiryDate) {
      formIsValid = false;
      error = { ...error, expiryDate: "field.empty" };
    }

    formState.form?.filter(hasGVPFormData).forEach((employee) => {
      if (!employee.ssn || !ssnPattern.test(employee.ssn)) {
        formIsValid = false;
        error = { ...error, ssn: "field.ssnInvalid" };
      }
    });

    setFormErrors(error);
    return formIsValid;
  };

  const saveForm = () => {
    if (handleValidation()) {
      scrollToTop();
      setPendingStatus(gvp?.status);
      setWaitingForStatusChange(true);
      updateGVP(getNormalizedFormState());
    }
  };

  const saveFormExit = () => {
    if (handleValidation()) {
      updateGVPLater(getNormalizedFormState());
      setNewGVP(0);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const editForm = () => {
    scrollToTop();
    setGVPEdit();
  };

  const backToGVPList = () => {
    scrollToTop();
    setNewGVP(0);
  };

  const signForm = () => {
    setSignDocument(false);
  };

  const handleSignSuccess = () => {
    scrollToTop();
    setNewGVP(0);
  };

  const signDocumentOpen = async (documentId: string) => {
    scrollToTop();
    let data: any = await sendSign(documentId);
    if (data?.status === "REQUESTED") {
      setHash(data?.hash);
      setSignDocument(true);
    }
    if (data?.status === "SUCCESS") {
      handleSignSuccess();
    }
  };

  const popupHandler = () => {
    setIsDocument((current) => !current);
  };

  const mobile = useMediaQuery(theme.breakpoints.down("sm"));

  return !mobile ? (
    <Box className={classes.gContainer}>
      {!gvpLoading ? (
        <>
          <ProfileDocumentViewer
            fileName={formState.fileName}
            isDocumentOpen={isDocumentOpen}
            closeHandler={popupHandler}
          />
        </>
      ) : null}
      <ProfileSignDocument
        isSignOpen={signDocument}
        hash={hash}
        closeHandler={signForm}
        successHandler={handleSignSuccess}
      />
      <Grid container spacing={2}>
        <Grid item sm={12}>
          <Box textAlign="right">
            <Button
              variant="contained"
              onClick={backToGVPList}
              size="large"
              className={classes.menuButtonDark}
            >
              <FormattedMessage id="register.back" />
            </Button>
          </Box>
          {waitingForStatusChange || gvp?.status === "REPORT" ? (
            <ProfileSpinner disableTimeout />
          ) : null}
          <Typography className={classes.titleBig}>
            {gvp?.status === "NEW"
              ? intl.formatMessage({ id: "profile.gvpStatusNew" })
              : null}

            {gvp?.status === "REPORT"
              ? intl.formatMessage({ id: "profile.gvpStatusReport" })
              : null}

            {gvp?.status === "SIGN"
              ? intl.formatMessage({ id: "profile.gvpStatusSign" })
              : null}

            <IconButton
              className={classes.buttonInfo}
              aria-label={intl.formatMessage({ id: "aria.info" })}
              aria-haspopup="true"
              onClick={popupHandler}
              color="inherit"
            >
              <InfoIcon />
            </IconButton>
          </Typography>
                <Typography className={classes.infoText}>
                  <FormattedMessage
                    id="profile.gvpInfo"
                    values={{ strong: (chunks) => <b>{chunks}</b> }}
                  />
                </Typography>
                <Typography className={classes.infoText}>
                  <FormattedMessage id="profile.gvpIntro" values={{ contractCode: profile.contractCode }} /> &nbsp;
                  { gvp?.status === "NEW" ?
                    <>
                      <Box style={{ width: "150px", paddingLeft: "5px", paddingRight: "5px" }} display="inline-block">
                        <InputSelectField
                          name="signReason"
                          title={intl.formatMessage({ id: "register.position" })}
                          enabled={gvp?.status === "NEW" ? true : false}
                          list={signReasons}
                          value={formState.signReason || ""}
                          error={formError.signReason}
                          formChange={handleSelectChange}
                        />
                      </Box> 
                      {formState.signReason === "savs variants" ? 
                      <Box style={{ width: "150px", paddingLeft: "5px", paddingRight: "5px" }} display="inline-block">
                        <InputTextField
                          name="signReasonCustom"
                          required={true}
                          title={intl.formatMessage({ id: "register.position" })}
                          value={(formState?.signReasonCustom || "").toUpperCase()}
                          error={formError.signReasonCustom}
                          enabled={gvp?.status === "NEW" ? true : false}
                          formChange={(ev: ChangeEvent<HTMLInputElement>): void =>
                            handleInputChange(ev)
                          }
                        />
                      </Box> 
                      : null}
                     </>
                  : formState.signReason === "savs variants" ? formState.signReasonCustom : formState.signReason } &nbsp;
                  {gvp?.status === "NEW" ? 
                  <Box style={{ width: "250px", paddingLeft: "5px", paddingRight: "5px" }} display="inline-block">
                    <InputTextField
                      name="signName"
                      required={true}
                      title={intl.formatMessage({ id: "profile.fullName" })}
                      value={toInitCap(formState?.signName || "")}
                      error={formError.signName}
                      enabled={gvp?.status === "NEW" ? true : false}
                      formChange={(ev: ChangeEvent<HTMLInputElement>): void =>
                        handleInputChange(ev)
                      }
                    />
                  </Box>
                  : formState.signName }
                  , 
                </Typography>
                <Typography className={classes.infoText}>
                  <FormattedMessage id="profile.gvpActsOn" /> &nbsp;
                  { gvp?.status === "NEW" ?
                    <Box style={{ width: "150px", paddingLeft: "5px", paddingRight: "5px" }} display="inline-block">
                    <InputSelectField 
                      name="signRole"
                      title={intl.formatMessage({ id: "profile.basis" })}
                      enabled={gvp?.status === "NEW" ? true : false}
                      list={signRole}
                      value={formState.signRole || ""}
                      error={formError.signRole}
                      formChange={handleSelectChange}
                    /> 
                    </Box>
                    : formState.signRole } &nbsp;
                  <FormattedMessage
                    id="profile.gvpGuaranteesUntil"
                    values={{ strong: (chunks) => <b>{chunks}</b> }}
                  />&nbsp;
                  { gvp?.status === "NEW" ?
                    <Box style={{ width: "110px", paddingLeft: "5px", paddingRight: "5px" }} display="inline-block">
                      <DatePicker
                        selected={expiryDate}
                        onChange={(date: Date) => setExpiryDate(date)}
                        selectsEnd
                        monthsShown={2}
                        locale={lv}
                        startDate={expiryDate}
                        minDate={moment().toDate()}
                        filterDate={isWeekday}
                        dateFormat="dd.MM.yyyy"
                        placeholderText={intl.formatMessage({ id: "profile.until" })}
                        className={classes.inputInput}
                      />
                      {formError.expiryDate ? (
                        <span className={classes.labelError}>
                          <FormattedMessage id={formError.expiryDate} />
                        </span>
                      ) : null}
                    </Box>
                    : formState.endDate 
                  } &nbsp;
                  <FormattedMessage id="profile.gvpFollowingEmployees" />
                </Typography>
                <Typography className={classes.infoText}>
                <FormattedMessage id="profile.gvpInvoiceDelivery" />
                </Typography>
                <Typography className={classes.infoText}>
                  <Box style={{ textAlign: "left"}} >
                  <Box style={{ width: "30px", paddingLeft: "5px", paddingRight: "5px" }} display="inline-block">
                  <Checkbox
                      onChange={handleInputChange}
                      name="sendPost"
                      id="sendPost"
                      disabled={gvp?.status === "NEW" ? false : true}
                      checked={formState.sendPost === 1 ? true : false}
                      className={classes.checkbox}
                      inputProps={{ "aria-label": intl.formatMessage({ id: "aria.primaryCheckbox" }) }}
                    />
                  </Box>
                  <FormattedMessage id="profile.gvpSendByPost" /> &nbsp;
                  {gvp?.status === "NEW" ? 
                    <Box style={{ width: "450px", paddingLeft: "5px", paddingRight: "5px" }} display="inline-block">
                      <InputTextField
                        name="sendPostAddress"
                        required={true}
                        title={intl.formatMessage({ id: "register.address" })}
                        value={(formState?.sendPostAddress || "").toUpperCase() || (profile.registrationAddress || "").toUpperCase()}
                        error={formError?.sendPostAddress}
                        enabled={gvp?.status === "NEW" ? true : false}
                        formChange={(ev: ChangeEvent<HTMLInputElement>): void =>
                          handleInputChange(ev)
                        }
                      />
                    </Box>
                  : formState?.sendPostAddress }
                  <br/>
                  <Box style={{ width: "30px", paddingLeft: "5px", paddingRight: "5px" }} display="inline-block">
                  <Checkbox
                      onChange={handleInputChange}
                      name="sendEmail"
                      id="sendEmail"
                      disabled={gvp?.status === "NEW" ? false : true}
                      checked={formState?.sendEmail === 1 ? true : false}
                      className={classes.checkbox}
                      inputProps={{ "aria-label": intl.formatMessage({ id: "aria.primaryCheckbox" }) }}
                    />
                  </Box>
                  <FormattedMessage id="profile.gvpSendByEmail" /> &nbsp;
                  {gvp?.status === "NEW" ? 
                  <Box style={{ width: "450px", paddingLeft: "5px", paddingRight: "5px" }} display="inline-block">
                    <InputTextField
                      name="sendEmailAddress"
                      required={true}
                      title={intl.formatMessage({ id: "profile.emailAddress" })}
                      value={(formState?.sendEmailAddress || "").toLocaleLowerCase() || (user.profile.email || "").toLocaleLowerCase()}
                      error={formError?.sendEmailAddress}
                      enabled={gvp?.status === "NEW" ? true : false}
                      formChange={(ev: ChangeEvent<HTMLInputElement>): void =>
                        handleInputChange(ev)
                      }
                    />
                  </Box>
                  : formState?.sendEmailAddress }
                  <br/>
                  <Box style={{ width: "30px", paddingLeft: "5px", paddingRight: "5px" }} display="inline-block">
                  <Checkbox
                      onChange={handleInputChange}
                      name="marketing"
                      id="marketing"
                      disabled={gvp?.status === "NEW" ? false : true}
                      checked={formState?.marketing === 1 ? true : false}
                      className={classes.checkbox}
                      inputProps={{ "aria-label": intl.formatMessage({ id: "aria.primaryCheckbox" }) }}
                    />
                  </Box>
                  <FormattedMessage id="profile.gvpMarketingConsent" />
                  </Box>
                </Typography>
        </Grid>
        <Grid item sm={12} spacing={2}>
          <Grid container className={classes.tableHeader} spacing={2}>
            <Grid item md={2}>
              <Typography className={classes.tableTitle}>
                <FormattedMessage id="register.name" />
              </Typography>
            </Grid>
            <Grid item md={2}>
              <Typography className={classes.tableTitle}>
                <FormattedMessage id="register.surname" />
              </Typography>
            </Grid>
            <Grid item md={2}>
              <Typography className={classes.tableTitle}>
                <FormattedMessage id="profile.employeeSSN" />
              </Typography>
            </Grid>
            <Grid item md={2}>
              <Typography className={classes.tableTitle}>
                <FormattedMessage id="register.email" />
              </Typography>
            </Grid>
            <Grid item md={2}>
              <Typography className={classes.tableTitle}>
                <FormattedMessage id="register.phone" />
              </Typography>
            </Grid>
            <Grid item md={2}>
              <Typography className={classes.tableTitle}>
                <FormattedMessage id="profile.actions" />
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item sm={12}>
          <Grid container spacing={2}>
            {!gvpLoading
              ? formState.form?.map((employee, index) => (
                  <>
                    <Grid item md={2}>
                      <InputTextField
                        name="name"
                        required={true}
                        value={toInitCap(employee?.name || "")}
                        error={formError.name}
                        enabled={gvp?.status === "NEW" ? true : false}
                        formChange={(ev: ChangeEvent<HTMLInputElement>): void =>
                          handleFormChange(ev, index)
                        }
                      />
                    </Grid>
                    <Grid item md={2}>
                      <InputTextField
                        name="surname"
                        required={true}
                        value={toInitCap(employee?.surname || "")}
                        error={formError.surname}
                        enabled={gvp?.status === "NEW" ? true : false}
                        formChange={(ev: ChangeEvent<HTMLInputElement>): void =>
                          handleFormChange(ev, index)
                        }
                      />
                    </Grid>
                    <Grid item md={2}>
                      <InputTextField
                        name="ssn"
                        required={true}
                        value={employee?.ssn}
                        error={formError.ssn}
                        enabled={gvp?.status === "NEW" ? true : false}
                        formChange={(ev: ChangeEvent<HTMLInputElement>): void =>
                          handleFormChange(ev, index)
                        }
                      />
                    </Grid>
                    <Grid item md={2}>
                      <InputTextField
                        name="email"
                        value={(employee?.email || "").toLocaleLowerCase()}
                        error={formError.email}
                        enabled={gvp?.status === "NEW" ? true : false}
                        formChange={(ev: ChangeEvent<HTMLInputElement>): void =>
                          handleFormChange(ev, index)
                        }
                      />
                    </Grid>
                    <Grid item md={2}>
                      <InputTextField
                        name="phone"
                        required={true}
                        value={employee?.phone}
                        error={formError.phone}
                        enabled={gvp?.status === "NEW" ? true : false}
                        formChange={(ev: ChangeEvent<HTMLInputElement>): void =>
                          handleFormChange(ev, index)
                        }
                      />
                    </Grid>
                    <Grid item md={2}>
                      {gvp?.status === "NEW" ?
                       <Box
                          textAlign="center"
                          onClick={() => deleteGVPLine(employee?.id)}
                        >
                          <DeleteForeverIcon className={classes.red} />
                        </Box>
                       : false}
                    </Grid>     
                  </>
                ))
              : null}
            <Grid item md={12}>
              <div ref={actionBoxRef}>
                <Box textAlign="right" pb={3}>
                  {gvp?.status === "NEW" ? (
                    <>
                      <Button
                        variant="contained"
                        onClick={() => addRow()}
                        size="large"
                        className={classes.menuButtonDark}
                      >
                        <FormattedMessage id="profile.gvpAdd" />
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => saveForm()}
                        size="large"
                        className={classes.button}
                      >
                        <FormattedMessage id="save" />
                      </Button>
                    </>
                  ) : null}

                  {gvp?.status === "REPORT" || gvp?.status === "SIGN" ? (
                    <Button
                      variant="contained"
                      onClick={() => editForm()}
                      size="large"
                      className={classes.button}
                    >
                      <FormattedMessage id="profile.gvpEdit" />
                    </Button>
                  ) : null}

                  {gvp?.status === "SIGN" ? (
                    <Button
                      variant="contained"
                      onClick={() => signDocumentOpen(gvp?.id.toString())}
                      size="large"
                      className={classes.button}
                    >
                      <FormattedMessage id="profile.gvpSign" />
                    </Button>
                  ) : null}
                  <Button
                    variant="contained"
                    onClick={() => saveFormExit()}
                    size="large"
                    className={classes.menuButtonDark}
                  >
                    <FormattedMessage id="profile.gvpSignLater" />
                  </Button>
                </Box>
              </div>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  ) : (
    <Box textAlign="center">
      <FormattedMessage id="profile.gvpDesktopOnly" />
    </Box>
  );
};

const mapStateToProps = ({ user }: ApplicationState) => ({
  gvp: user.profile.gvp,
  gvpLoading: user.gvpLoading,
  login: user.login,
  profile: user.profile,
  user: user.user,
});

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => {
  return {
    getGVP: () => {
      dispatch(getGVP());
    },
    getGVPStatus: () => {
      dispatch(getGVPStatus());
    },
    setGVPEdit: () => {
      dispatch(setGVPEdit());
    },
    updateGVP: (payload: any) => {
      dispatch(updateGVP(payload));
    },
    updateGVPLater: (payload: any) => {
      dispatch(updateGVPLater(payload));
    },
    deleteGVPLine: (clientAgreementFormEntryId: number) => {
      dispatch(deleteGVPLine(clientAgreementFormEntryId));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ProfileGVP);
